import React, { useEffect, useState, useRef } from "react";
import { fmtDate } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, CURRENCIES } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { PhotoCompare } from "@/components/booking/PhotoCompare";
import { AddCardInline } from "@/components/booking/AddCardInline";
import { ClientCharts } from "@/components/booking/ClientCharts";
import { MedicalAlerts, MedicalHistory } from "@/components/booking/MedicalAlerts";
import { ClientImport } from "@/components/booking/ClientImport";
import { CancelDialog } from "@/components/booking/CancelDialog";
import { ClientPackages } from "@/components/booking/ClientPackages";
import { BanClient } from "@/components/booking/BanClient";
import { ClientSnapshot } from "@/components/booking/ClientSnapshot";
import { ClientVisits } from "@/components/booking/ClientVisits";
import { ClientRewards } from "@/components/booking/ClientRewards";
import { SourcePicker } from "@/components/booking/SourcePicker";
import { FormAnswersDialog, formStatus, aftercareStatus } from "@/components/booking/FormAnswers";
import { ClientInvoices } from "@/components/booking/ClientInvoices";
import { Plus, Trash2, ChevronLeft, Mail, Phone, Wallet, Gift, Trophy as TrophyIcon, History, Package, CalendarDays, Save, AlertTriangle, ImagePlus, Images, FileText, Send, Link as LinkIcon, GitCompare, CreditCard, Ban, MessageSquare } from "lucide-react";import { toast } from "sonner";
import { PortalInviteButton, MergeClientDialog } from "@/components/booking/ClientTools";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const photoSrc = (id) => `${API_BASE}/clients/photos/${id}/file?auth=${localStorage.getItem("pc_token")}`;

const MSG_KIND_LABEL = {
  confirmation: "Booking confirmation", reminder: "Reminder", reschedule: "Reschedule", cancellation: "Cancellation",
  no_show: "No-show follow-up", rebook: "Rebook", review: "Review request", waitlist: "Waitlist", receipt: "Voucher receipt",
  aftercare: "Aftercare", consult: "Consult form", survey: "Survey", manual: "Manual",
};
const MSG_STATUS = {
  sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  queued: { label: "Queued", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  simulated: { label: "Simulated", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 border-red-200" },
  skipped_no_credit: { label: "No credit", cls: "bg-red-100 text-red-700 border-red-200" },
};
const fmtMsgWhen = (iso) => { try { return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso || ""; } };

const empty = { name: "", email: "", phone: "", source: "", referred_by_client_id: "", content_consent: false, notes: "", dob: "", medical_history: "", marketing_opt_in: false, is_legacy: false, is_steroid_use: false, has_metabolic_disorder: false, online_booking_blocked: false, online_booking_block_reason: "" };

// One row per visit: services sharing a group_id on the same day collapse into "Brow + Brazilian", priced as the whole visit.
const groupVisits = (rows) => {
  const out = [], byKey = {};
  [...rows].sort((a, b) => (a.start || "").localeCompare(b.start || "")).forEach((b) => {
    const key = b.group_id ? `${b.group_id}|${(b.start || "").slice(0, 10)}` : b.id;
    if (byKey[key]) {
      const v = byKey[key];
      v.service_name = `${v.service_name} + ${b.service_name}`;
      v.visit_count += 1;
      v.price = (v.price || 0) + (b.price || 0);
      if (b.paid) { v.paid = true; v.total_paid = (v.total_paid || 0) + (b.total_paid || 0); }
      if (b.status === "checked_out") v.status = "checked_out";
      v.line_items = [...(v.line_items || []), ...(b.line_items || [])];
      v.members = [...v.members, b];
    } else {
      byKey[key] = { ...b, visit_count: 1, members: [b] };
      out.push(byKey[key]);
    }
  });
  return out;
};
const isNewClient = (c) => (c.created_at || "").slice(0, 7) === new Date().toISOString().slice(0, 7);

export default function Clients() {
  const { business, isAdmin } = useAuth();
  const cur = business?.currency || "AUD";
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null); // client file data {client, bookings, vouchers, packages}
  const [ef, setEf] = useState(empty);
  const [addCredit, setAddCredit] = useState("");
  const [photos, setPhotos] = useState([]);
  const [photoLabel, setPhotoLabel] = useState("before");
  const [photoNote, setPhotoNote] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInput = useRef(null);
  const [fileTab, setFileTab] = useState("visits");
  const [msgText, setMsgText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [cmpBefore, setCmpBefore] = useState("");
  const [cmpAfter, setCmpAfter] = useState("");
  const [cards, setCards] = useState([]);
  const [cardBusy, setCardBusy] = useState(false);
  const [consults, setConsults] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [consultTpl, setConsultTpl] = useState("");
  const [sendingConsult, setSendingConsult] = useState(false);
  const [aftercare, setAftercare] = useState([]);
  const [viewForm, setViewForm] = useState(null);
  const [acTpls, setAcTpls] = useState([]);
  const [acTpl, setAcTpl] = useState("");
  const [sendingAc, setSendingAc] = useState(false);
  const [params, setParams] = useSearchParams();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEfV = (k, v) => setEf((f) => ({ ...f, [k]: v }));

  const [tagFilter, setTagFilter] = useState(params.get("filter") || "");
  const load = () => api.get("/clients", { params: tagFilter && tagFilter !== "banned" ? { tag: tagFilter } : {} }).then(({ data }) => setClients(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tagFilter]);

  const [sendingDetails, setSendingDetails] = useState("");
  const sendDetails = async (b) => {
    setSendingDetails(b.id);
    try { await api.post(`/bookings/${b.id}/send-details`); toast.success(`Appointment details texted to ${file?.client?.name?.split(" ")[0] || "client"}`); openFile(b.client_id || file.client.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the text"); }
    finally { setSendingDetails(""); }
  };
  const openFile = async (id) => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setFile(data);
      setEf({ ...empty, ...data.client });
      setAddCredit("");
      setFileTab("visits");
      setCompareMode(false); setCmpBefore(""); setCmpAfter("");
      loadPhotos(id);
      loadCards(id);
      loadConsults(id);
      loadAftercare(id);
      api.get("/forms/templates").then(({ data }) => setTemplates(data)).catch(() => {});
      api.get("/aftercare/templates").then(({ data }) => setAcTpls(data)).catch(() => {});
    } catch { toast.error("Couldn't open client file"); }
  };

  const [cancelB, setCancelB] = useState(null);
  const cancelClientBooking = (b) => setCancelB(b);
  const runClientCancel = async ({ reason, note, notify }) => {
    try {
      await api.patch(`/bookings/${cancelB.id}/status`, { status: "cancelled", reason, note, notify });
      toast.success(notify ? "Booking cancelled" : "Booking cancelled — no text sent");
      openFile(file.client.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel booking"); }
  };
  const deleteClientSeries = async (b) => {
    if (!window.confirm("Delete the ENTIRE recurring series (every date of this repeat)? This can't be undone.")) return;
    try {
      const { data } = await api.post(`/bookings/${b.id}/series-action`, { action: "delete", scope: "all" });
      toast.success(`Deleted ${data.affected} appointment${data.affected === 1 ? "" : "s"}`);
      openFile(file.client.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete series"); }
  };
  const loadConsults = (id) => api.get(`/clients/${id}/consultations`).then(({ data }) => setConsults(data)).catch(() => setConsults([]));
  const sendConsult = async () => {
    if (!consultTpl) return toast.error("Choose a form");
    setSendingConsult(true);
    try {
      const { data } = await api.post(`/clients/${file.client.id}/consultations`, { template_id: consultTpl, via: file.client.phone ? "sms" : "link", origin: window.location.origin });
      const link = `${window.location.origin}/consult/${data.token}`;
      try { await navigator.clipboard.writeText(link); } catch (_) {}
      toast.success(file.client.phone ? "Sent by SMS — link copied too" : "Form created — link copied to clipboard");
      setConsultTpl(""); loadConsults(file.client.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
    finally { setSendingConsult(false); }
  };
  const copyConsult = (c) => { const link = `${window.location.origin}/consult/${c.token}`; navigator.clipboard.writeText(link).then(() => toast.success("Link copied")); };
  const deleteConsult = async (id) => { if (!window.confirm("Delete this consultation? This can't be undone.")) return; await api.delete(`/clients/${file.client.id}/consultations/${id}`); setConsults((cs) => cs.filter((c) => c.id !== id)); toast.success("Removed"); };
  const loadAftercare = (id) => api.get(`/clients/${id}/aftercare`).then(({ data }) => setAftercare(data)).catch(() => setAftercare([]));
  const sendAftercare = async () => {
    if (!acTpl) return toast.error("Choose aftercare to send");
    setSendingAc(true);
    try {
      const { data } = await api.post(`/clients/${file.client.id}/aftercare`, { template_id: acTpl, via: file.client.phone ? "sms" : "link", origin: window.location.origin });
      const link = `${window.location.origin}/aftercare/${data.token}`;
      try { await navigator.clipboard.writeText(link); } catch (_) {}
      toast.success(file.client.phone ? "Sent by SMS — link copied too" : "Aftercare created — link copied to clipboard");
      setAcTpl(""); loadAftercare(file.client.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); }
    finally { setSendingAc(false); }
  };
  const copyAftercare = (a) => { const link = `${window.location.origin}/aftercare/${a.token}`; navigator.clipboard.writeText(link).then(() => toast.success("Link copied")); };
  const deleteAftercare = async (id) => { if (!window.confirm("Delete this aftercare? This can't be undone.")) return; await api.delete(`/clients/${file.client.id}/aftercare/${id}`); setAftercare((xs) => xs.filter((x) => x.id !== id)); toast.success("Removed"); };
  const loadPhotos = (id) => api.get(`/clients/${id}/photos`).then(({ data }) => {
    const all = data.filter((p) => !p.chart_id);
    setPhotos(all);
    const b = all.find((p) => p.label === "before") || all[all.length - 1];
    const a = all.find((p) => p.label === "after") || all[0];
    if (b) setCmpBefore(b.id);
    if (a) setCmpAfter(a.id);
  }).catch(() => setPhotos([]));
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !file) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("label", photoLabel);
    fd.append("note", photoNote);
    setUploadingPhoto(true);
    try {
      await api.post(`/clients/${file.client.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoNote("");
      await loadPhotos(file.client.id);
      toast.success("Photo added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't upload photo"); }
    finally { setUploadingPhoto(false); if (photoInput.current) photoInput.current.value = ""; }
  };
  const deletePhoto = async (pid) => {
    try { await api.delete(`/clients/${file.client.id}/photos/${pid}`); setPhotos((ps) => ps.filter((p) => p.id !== pid)); toast.success("Photo removed"); }
    catch { toast.error("Couldn't remove photo"); }
  };
  const loadCards = (id) => api.get(`/clients/${id}/cards`).then(({ data }) => setCards(data)).catch(() => setCards([]));
  const [addingCard, setAddingCard] = useState(false);
  const addCard = () => setAddingCard(true);
  // Fallback: Stripe-hosted page (asks the client for email — use only when typing the card in isn't possible).
  const addCardHosted = async () => {
    setCardBusy(true);
    try {
      const { data } = await api.post(`/clients/${file.client.id}/cards/setup`, { origin_url: window.location.origin, return_path: `/clients?client=${file.client.id}` });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start card setup"); setCardBusy(false); }
  };
  const removeCard = async (pm) => {
    if (!window.confirm("Remove this saved card?")) return;
    try { await api.delete(`/clients/${file.client.id}/cards/${pm}`); setCards((cs) => cs.filter((c) => c.id !== pm)); toast.success("Card removed"); }
    catch { toast.error("Couldn't remove card"); }
  };
  const syncMailchimp = async () => {
    try { await api.post(`/clients/${file.client.id}/sync-mailchimp`); toast.success("Synced to Mailchimp"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't sync — connect Mailchimp in Integrations"); }
  };
  const [opening, setOpening] = useState(!!params.get("client"));
  useEffect(() => {
    const cid = params.get("client");
    if (cid) { setOpening(true); openFile(cid).finally(() => setOpening(false)); }
    const card = params.get("card");
    if (card === "added") { toast.success("Card saved securely"); params.delete("card"); setParams(params, { replace: true }); }
    else if (card === "cancel") { toast.info("Card setup cancelled"); params.delete("card"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, [params]);

  const closeFile = () => { setFile(null); if (params.get("client")) { params.delete("client"); setParams(params, { replace: true }); } };

  const sendClientMsg = async () => {
    const t = msgText.trim();
    if (!t || !file?.client?.id) return;
    setSendingMsg(true);
    try {
      const { data } = await api.post(`/clients/${file.client.id}/message`, { body: t });
      const st = data?.status;
      if (st === "sent" || st === "queued") toast.success("Message sent");
      else if (st === "simulated") toast.info("Sent (simulated — live delivery pending Twilio approval)");
      else toast.warning(`Status: ${st || "unknown"}`);
      setMsgText("");
      const { data: fresh } = await api.get(`/clients/${file.client.id}`);
      setFile((f) => ({ ...f, messages: fresh.messages || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send message"); }
    finally { setSendingMsg(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try { await api.post("/clients", form); setForm(empty); setOpen(false); load(); toast.success("Client added"); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.client_id) {
        toast.message(d.message, { description: "Open their existing file instead of making a duplicate?", action: { label: "Open file", onClick: () => { setForm(empty); setOpen(false); openFile(d.client_id); } }, duration: 10000 });
        return;
      }
      toast.error(formatApiError(d) || "Couldn't add client");
    }
  };
  const saveFile = async () => {
    try {
      const { data } = await api.put(`/clients/${file.client.id}`, { name: ef.name, email: ef.email, phone: ef.phone, source: ef.source, notes: ef.notes, dob: ef.dob, medical_history: ef.medical_history, marketing_opt_in: !!ef.marketing_opt_in, is_legacy: !!ef.is_legacy, is_steroid_use: !!ef.is_steroid_use, has_metabolic_disorder: !!ef.has_metabolic_disorder, online_booking_blocked: !!ef.online_booking_blocked, online_booking_block_reason: ef.online_booking_block_reason || "", referred_by_client_id: ef.referred_by_client_id || "", referred_by_name: ef.referred_by_name || "", content_consent: !!ef.content_consent });
      setFile((f) => ({ ...f, client: { ...f.client, ...data } })); load(); openFile(file.client.id); toast.success("Client saved");
    } catch { toast.error("Couldn't save client"); }
  };
  const applyCredit = async () => {
    const amt = parseFloat(addCredit);
    if (!amt) return;
    try {
      const { data } = await api.post(`/clients/${file.client.id}/credit`, { amount: amt, reason: amt > 0 ? "Bonus credit (no payment)" : "Manual adjustment" });
      setFile((f) => ({ ...f, client: { ...f.client, credit_balance: data.credit_balance } }));
      setAddCredit("");
      toast.success(amt > 0 ? `Added ${fmtCurrency(amt, cur, 2)} bonus credit` : "Credit updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update credit"); }
  };
  const remove = async (c) => { try { await api.delete(`/clients/${c.id}`); load(); toast.success("Removed"); } catch { toast.error("Failed"); } };

  const filtered = clients.filter((c) => {
    if (tagFilter === "banned") return !!c.banned;
    if (c.banned) return false;
    const term = q.trim().toLowerCase();
    if (!term) return true;
    const digits = q.replace(/\D/g, "");
    return c.name.toLowerCase().includes(term)
      || (c.email || "").toLowerCase().includes(term)
      || (digits.length >= 3 && (c.phone || "").replace(/\D/g, "").includes(digits));
  })
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base", numeric: true }));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {!file && !opening && <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Clients</h1>
          <p className="text-muted-foreground mt-1">{clients.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <ClientImport onDone={load} />}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-lg" data-testid="add-client-button"><Plus className="h-4 w-4 mr-1.5" /> New client</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New client</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Input placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" data-testid="client-name" />
              <Input placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-lg" data-testid="client-email" />
              <Input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-lg" data-testid="client-phone" />
              <SourcePicker value={form.source} onChange={(v) => set("source", v)} referredBy={form.referred_by_client_id} onReferredBy={(v) => set("referred_by_client_id", v)} clients={clients} testId="client-source" />
              <div><label className="text-xs text-muted-foreground">Date of birth</label><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className="h-11 rounded-lg" data-testid="client-dob" /></div>
              <textarea placeholder="Medical history / notes" value={form.medical_history} onChange={(e) => set("medical_history", e.target.value)} className="w-full rounded-lg border border-input bg-background p-3 text-sm min-h-[80px]" data-testid="client-medical" />
            </div>
            <DialogFooter><Button onClick={save} className="rounded-lg" data-testid="client-save">Save client</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Input placeholder="Search by name or phone…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 rounded-lg mb-3 max-w-sm" data-testid="client-search" />

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="client-tag-filters">
        {[["", "All"], ["legacy", "Legacy"], ["lapsed", "Lapsed"], ["overdue", "Overdue"], ["high-spender", "High spender"], ["has-credit", "Has credit"], ["voucher-buyer", "Voucher buyer"], ["products", "Bought products"], ["no-visits", "No visits"], ["banned", "Banned"]].map(([val, label]) => (
          <button key={val} onClick={() => setTagFilter(val)}
            className={`text-xs rounded-full border px-3 py-1 transition-colors ${tagFilter === val ? "bg-primary text-primary-foreground border-primary" : "bg-muted/40 hover:bg-primary/10 border-border"}`}
            data-testid={`tag-filter-${val || "all"}`}>{label}</button>
        ))}
        {tagFilter && <span className="text-xs text-muted-foreground self-center ml-1">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>}
      </div>

      <Card className="rounded-2xl border bg-card divide-y divide-border shadow-sm" data-testid="clients-list">
        {filtered.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-4 hover:bg-muted/40 cursor-pointer" data-testid={`client-row-${c.id}`} onClick={() => openFile(c.id)}>
            <div>
              <div className="font-medium flex items-center gap-2">
                {c.name}
                {isNewClient(c) && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary/15 text-primary" data-testid={`client-new-${c.id}`}>New</span>}
                {(c.no_show_count || 0) > 0 && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 flex items-center gap-1" data-testid={`client-noshow-${c.id}`}><AlertTriangle className="h-3 w-3" />{c.no_show_count} no-show{c.no_show_count > 1 ? "s" : ""}</span>}
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-0.5">
                {c.email && <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-primary hover:underline" data-testid={`client-email-${c.id}`}><Mail className="h-3 w-3" />{c.email}</a>}
                {c.phone && <a href={`sms:${c.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-primary hover:underline" data-testid={`client-sms-${c.id}`}><Phone className="h-3 w-3" />{c.phone}</a>}
                {(c.credit_balance || 0) > 0 && <span className="flex items-center gap-1 text-primary"><Wallet className="h-3 w-3" />{fmtCurrency(c.credit_balance, cur, 2)}</span>}
                {c.source && <span className="px-1.5 rounded bg-muted">{c.source}</span>}
              </div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); remove(c); }} className="text-muted-foreground hover:text-destructive" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-8 text-center text-muted-foreground">No clients found.</div>}
      </Card>

      </>}
      {/* Client file */}
      {!file && opening && <div className="py-24 text-center text-sm text-muted-foreground" data-testid="client-file-loading">Opening client file…</div>}
      {file && <div className="space-y-5" data-testid="client-file-page">
            <Button variant="ghost" size="sm" className="rounded-lg -ml-2" onClick={closeFile} data-testid="client-file-back"><ChevronLeft className="h-4 w-4 mr-1" />All clients</Button>
            <div className="space-y-1">
              <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight flex items-center gap-2">
                {file.client.name}
                {file.client.banned && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid="file-banned-flag">Banned</span>}
                {(file.client.no_show_count || 0) > 0 && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-600 flex items-center gap-1" data-testid="file-noshow-flag"><AlertTriangle className="h-3 w-3" />{file.client.no_show_count} no-show{file.client.no_show_count > 1 ? "s" : ""}</span>}
              </h1>
              <p className="text-sm text-muted-foreground">Client file · added {((file.client.created_at || "").slice(0, 16).replace("T", " ")) || "—"} · {file.client.visit_count || 0} visit{(file.client.visit_count || 0) === 1 ? "" : "s"} · {fmtCurrency(file.client.total_spent || 0, cur, 2)} spent</p>
            </div>

            {(file.client.phone || file.client.email) && (
              <div className="flex flex-wrap gap-2">
                {file.client.phone && <a href={`sms:${file.client.phone}`} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary hover:text-primary transition-colors" data-testid="file-sms-link"><MessageSquare className="h-3.5 w-3.5" />Text</a>}
                {file.client.phone && <a href={`tel:${file.client.phone}`} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary hover:text-primary transition-colors" data-testid="file-call-link"><Phone className="h-3.5 w-3.5" />Call {file.client.phone}</a>}
                {file.client.email && <a href={`mailto:${file.client.email}`} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary hover:text-primary transition-colors" data-testid="file-email-link"><Mail className="h-3.5 w-3.5" />Email</a>}
                <PortalInviteButton client={file.client} />
                {isAdmin && <MergeClientDialog client={file.client} onMerged={() => openFile(file.client.id)} />}
              </div>
            )}

            <MedicalAlerts clientId={file.client.id} />
            <ClientSnapshot client={file.client} onSaved={() => openFile(file.client.id)} />

            <Tabs value={fileTab} onValueChange={setFileTab} className="mt-1">
              <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full h-auto" data-testid="file-tabs">
                <TabsTrigger value="visits" data-testid="tab-visits">Visits</TabsTrigger>
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
                <TabsTrigger value="forms" data-testid="tab-forms">Forms{consults.filter((c) => c.status !== "completed").length > 0 && <span className="ml-1.5 inline-flex h-4 min-w-4 px-1 items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="forms-outstanding-badge">{consults.filter((c) => c.status !== "completed").length}</span>}</TabsTrigger>
                <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
                <TabsTrigger value="rewards" data-testid="tab-rewards"><TrophyIcon className="h-3.5 w-3.5 mr-1" />Rewards</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-4 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Name</label><Input value={ef.name} onChange={(e) => setEfV("name", e.target.value)} className="h-10 rounded-lg" data-testid="file-name" /></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Phone</label><Input value={ef.phone || ""} onChange={(e) => setEfV("phone", e.target.value)} className="h-10 rounded-lg" data-testid="file-phone" /></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Email</label><Input value={ef.email || ""} onChange={(e) => setEfV("email", e.target.value)} className="h-10 rounded-lg" data-testid="file-email" /></div>
                  <div className="space-y-1"><label className="text-xs text-muted-foreground">Date of birth</label><Input type="date" value={ef.dob || ""} onChange={(e) => setEfV("dob", e.target.value)} className="h-10 rounded-lg" data-testid="file-dob" /></div>
                </div>

                <div className="rounded-lg border p-3 space-y-2" data-testid="file-referral">
                  <div className="text-sm font-medium">How they found us</div>
                  <SourcePicker value={ef.source || ""} onChange={(v) => setEfV("source", v)} referredBy={ef.referred_by_client_id} onReferredBy={(v) => setEfV("referred_by_client_id", v)} clients={clients} excludeId={file.client.id} testId="file-source" />
                  {!ef.referred_by_client_id && ef.referred_by_name && <div className="text-xs text-muted-foreground">Client said: “{ef.referred_by_name}” — search above to link them.</div>}
                </div>
                <Button onClick={saveFile} className="rounded-lg w-full sm:w-auto" data-testid="file-save"><Save className="h-4 w-4 mr-1.5" />Save details</Button>
                <div className="flex items-center justify-between rounded-lg border p-3" data-testid="file-content-consent">
                  <div><div className="text-sm font-medium">Content consent</div><div className="text-xs text-muted-foreground">Happy for photos / results to be used on socials</div></div>
                  <Switch checked={!!ef.content_consent} onCheckedChange={(v) => setEfV("content_consent", v)} data-testid="file-content-consent-toggle" />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3" data-testid="file-marketing">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <div>
                      <div className="text-sm font-medium">Marketing opt-in</div>
                      <div className="text-xs text-muted-foreground">Include this client in email marketing (Mailchimp)</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ef.marketing_opt_in && ef.email && <Button size="sm" variant="outline" className="rounded-lg" onClick={syncMailchimp} data-testid="file-sync-mailchimp">Add to Mailchimp</Button>}
                    <Switch checked={!!ef.marketing_opt_in} onCheckedChange={(v) => setEfV("marketing_opt_in", v)} data-testid="file-opt-in" />
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3" data-testid="file-legacy">
                  <div className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-amber-500" />
                    <div>
                      <div className="text-sm font-medium">Legacy client</div>
                      <div className="text-xs text-muted-foreground">Grandfathered pricing — auto-applies your legacy discount at checkout</div>
                    </div>
                  </div>
                  <Switch checked={!!ef.is_legacy} onCheckedChange={(v) => setEfV("is_legacy", v)} data-testid="file-legacy-toggle" />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3" data-testid="file-steroid">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <div>
                      <div className="text-sm font-medium">Anabolic steroid use</div>
                      <div className="text-xs text-muted-foreground">History or current use — raises a medical alert</div>
                    </div>
                  </div>
                  <Switch checked={!!ef.is_steroid_use} onCheckedChange={(v) => setEfV("is_steroid_use", v)} data-testid="file-steroid-toggle" />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3" data-testid="file-metabolic">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <div>
                      <div className="text-sm font-medium">Metabolic / hormone disorder</div>
                      <div className="text-xs text-muted-foreground">e.g. PCOS / endometriosis — raises a medical alert</div>
                    </div>
                  </div>
                  <Switch checked={!!ef.has_metabolic_disorder} onCheckedChange={(v) => setEfV("has_metabolic_disorder", v)} data-testid="file-metabolic-toggle" />
                </div>

                <div className="rounded-lg border p-3 space-y-2" data-testid="file-online-block">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ban className={`h-4 w-4 ${ef.online_booking_blocked ? "text-red-500" : "text-muted-foreground"}`} />
                      <div>
                        <div className="text-sm font-medium">Block online booking</div>
                        <div className="text-xs text-muted-foreground">Stop this client self-booking online (they can still be booked in-clinic)</div>
                      </div>
                    </div>
                    <Switch checked={!!ef.online_booking_blocked} onCheckedChange={(v) => setEfV("online_booking_blocked", v)} data-testid="file-online-block-toggle" />
                  </div>
                  {ef.online_booking_blocked && (
                    <Input value={ef.online_booking_block_reason || ""} onChange={(e) => setEfV("online_booking_block_reason", e.target.value)} placeholder="Reason (optional, for your team)" className="h-9 rounded-lg text-sm" data-testid="file-online-block-reason" />
                  )}
                </div>

                {isAdmin && <BanClient client={file.client} onChanged={() => openFile(file.client.id)} />}

                <label className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm cursor-pointer" data-testid="client-not-expected-back">
                  <span><span className="font-medium">Not expected back</span><span className="block text-[11px] text-muted-foreground">One-off client (e.g. a single treatment while visiting). Excluded from rebooking and retention figures and from rebook texts.</span></span>
                  <Switch checked={!!file.client.not_expected_back} onCheckedChange={async (v) => { try { await api.put(`/clients/${file.client.id}`, { name: file.client.name, not_expected_back: v }); toast.success(v ? "Marked as not expected back" : "Back in the rebooking numbers"); setFile((f) => ({ ...f, client: { ...f.client, not_expected_back: v } })); } catch { toast.error("Couldn't update"); } }} data-testid="client-not-expected-back-switch" />
                </label>

                <div className="rounded-lg border p-3"><MedicalHistory clientId={file.client.id} /></div>

                {(file.client.marketing_tags || []).length > 0 && (
                  <div className="rounded-lg border p-3" data-testid="file-tags">
                    <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Auto tags</div>
                    <div className="flex flex-wrap gap-1.5">
                      {file.client.marketing_tags.map((t) => {
                        const tone = t === "legacy" ? "bg-amber-100 text-amber-700 border-amber-200"
                          : t === "high-spender" ? "bg-violet-100 text-violet-700 border-violet-200"
                          : t === "lapsed" ? "bg-red-100 text-red-700 border-red-200"
                          : t === "overdue" ? "bg-orange-100 text-orange-700 border-orange-200"
                          : t === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                          : "bg-primary/10 text-primary border-primary/20";
                        return <span key={t} className={`text-xs font-medium px-2 py-0.5 rounded-full border ${tone}`} data-testid={`file-tag-${t}`}>{t}</span>;
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">These sync to Mailchimp (as tags) and Klaviyo (as profile properties) for targeting campaigns.</p>
                  </div>
                )}

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="text-sm">
                    <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /><span>Account credit</span><span className="font-heading font-bold tabular-nums" data-testid="file-credit-balance">{fmtCurrency(file.client.credit_balance || 0, cur, 2)}</span></div>
                    {(file.held_deposits || []).length > 0 && <div className="text-[11px] text-primary mt-0.5" data-testid="file-held-deposits">Includes {file.held_deposits.length} paid deposit{file.held_deposits.length > 1 ? "s" : ""} ({fmtCurrency(file.held_deposits.reduce((a, d) => a + (d.deposit_amount || 0), 0), cur, 2)}) held as credit until the booking is checked out.</div>}
                    {isAdmin && <div className="text-[11px] text-muted-foreground mt-0.5">Add bonus/goodwill credit — no payment taken. Use +5 to add, −5 to remove.</div>}
                    {(file.credit_ledger || []).length > 0 && (
                      <details className="mt-1"><summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground" data-testid="file-credit-history-toggle">Credit history</summary>
                        <ul className="mt-1 space-y-0.5" data-testid="file-credit-history">{file.credit_ledger.slice(0, 10).map((l) => <li key={l.id} className="text-[11px] flex justify-between gap-3"><span className="text-muted-foreground truncate">{l.reason}</span><span className={`tabular-nums shrink-0 ${l.amount < 0 ? "text-rose-600" : "text-emerald-700"}`}>{l.amount < 0 ? "−" : "+"}{fmtCurrency(Math.abs(l.amount || 0), cur, 2)}</span></li>)}</ul>
                      </details>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <div className="relative w-28"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{CURRENCIES[cur]?.symbol || "$"}</span>
                        <Input type="number" placeholder="+/−" value={addCredit} onChange={(e) => setAddCredit(e.target.value)} className="h-9 rounded-lg pl-6 tabular-nums" data-testid="file-add-credit" /></div>
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={applyCredit} data-testid="file-apply-credit">Add credit</Button>
                    </div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Gift className="h-3.5 w-3.5" />Vouchers</div>
                    {(file.vouchers || []).map((v) => <div key={v.id} className="flex justify-between text-sm" data-testid={`file-voucher-${v.id}`}><span>{v.code}</span><span className="tabular-nums">{fmtCurrency(v.balance, cur, 2)}</span></div>)}
                    {(!file.vouchers || file.vouchers.length === 0) && <div className="text-sm text-muted-foreground">None</div>}
                  </div>
                  <div>
                    <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />Packages</div>
                    <ClientPackages packages={file.packages} cur={cur} isAdmin={isAdmin} onChanged={() => openFile(file.client.id)} />
                  </div>
                </div>

                <div className="rounded-lg border p-3" data-testid="file-cards-section">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" />Cards on file</div>
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={addCard} disabled={cardBusy} data-testid="add-card-button"><Plus className="h-3.5 w-3.5 mr-1" />{cardBusy ? "Opening…" : "Add card"}</Button>
                  </div>
                  {addingCard && (
                    <div className="mb-3 rounded-lg bg-muted/40 p-3">
                      <AddCardInline clientId={file.client.id} onSaved={() => { setAddingCard(false); loadCards(file.client.id); }} onCancel={() => setAddingCard(false)} />
                      <button type="button" onClick={addCardHosted} disabled={cardBusy} className="mt-2 text-[11px] text-muted-foreground underline underline-offset-2" data-testid="add-card-hosted">{cardBusy ? "Opening…" : "Or open Stripe's secure page instead"}</button>
                    </div>
                  )}
                  {cards.length === 0 && !addingCard ? (
                    <div className="text-sm text-muted-foreground">No saved cards. Add one for one-click checkout — just the card number, expiry and CVC.</div>
                  ) : cards.length === 0 ? null : (
                    <div className="space-y-1.5">
                      {cards.map((c) => (
                        <div key={c.id} className="flex items-center justify-between text-sm" data-testid={`card-${c.id}`}>
                          <span className="capitalize flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" />{c.brand} •••• {c.last4} <span className="text-muted-foreground text-xs">exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span></span>
                          <button onClick={() => removeCard(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`card-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-2">Cards are stored securely by Stripe — charge them in one click at checkout.</p>
                </div>
              </TabsContent>

              <TabsContent value="visits" className="mt-4" data-testid="file-visits-tab">
                <ClientVisits clientId={file.client.id} clientName={file.client.name} cur={cur} events={file.events || []} onSendDetails={sendDetails} onCancel={cancelClientBooking} onDeleteSeries={deleteClientSeries} sending={sendingDetails} reloadKey={file.bookings?.length} />
              </TabsContent>

              <TabsContent value="forms" className="mt-4" data-testid="file-consults-section">
                {consults.some((c) => c.status !== "completed") && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm mb-3" data-testid="forms-outstanding">
                    <b>{consults.filter((c) => c.status !== "completed").length}</b> form{consults.filter((c) => c.status !== "completed").length === 1 ? "" : "s"} still to be completed: {consults.filter((c) => c.status !== "completed").map((c) => c.name).join(", ")}
                  </div>
                )}
                <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2"><FileText className="h-3.5 w-3.5" />Consultation forms ({consults.length})</div>
                <div className="flex items-center gap-2 mb-2">
                  <Select value={consultTpl} onValueChange={setConsultTpl}>
                    <SelectTrigger className="h-9 rounded-lg flex-1 text-sm" data-testid="consult-template"><SelectValue placeholder="Choose a form to send…" /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="rounded-lg" disabled={sendingConsult} onClick={sendConsult} data-testid="send-consult-button"><Send className="h-4 w-4 mr-1.5" />Send</Button>
                </div>
                {consults.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2">No consultations sent yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {consults.map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-sm border-b border-border/50 pb-1" data-testid={`consult-${c.id}`}>
                        <span className="flex items-center gap-2 flex-wrap">{c.name}
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${formStatus(c).cls}`} data-testid={`consult-status-${c.id}`}>{formStatus(c).label}</span>
                          {c.sent_via && <span className="text-[10px] text-muted-foreground">{c.origin === "automation" ? "auto-sent" : c.sent_via === "sms" ? "via SMS" : "link"}</span>}
                        </span>
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <span className="text-xs">{c.status === "completed" ? `done ${fmtDate(c.completed_at)}` : `sent ${fmtDate(c.sent_at)}`}</span>
                          <button onClick={() => setViewForm(c.id)} className="text-xs text-primary hover:underline" data-testid={`consult-view-${c.id}`}>{c.status === "completed" ? "View answers" : "View"}</button>
                          <button onClick={() => copyConsult(c)} className="hover:text-foreground" title="Copy link" data-testid={`consult-copy-${c.id}`}><LinkIcon className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteConsult(c.id)} className="hover:text-destructive" data-testid={`consult-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2 mt-6"><FileText className="h-3.5 w-3.5" />Aftercare instructions ({aftercare.length})</div>
                <div className="flex items-center gap-2 mb-2">
                  <Select value={acTpl} onValueChange={setAcTpl}>
                    <SelectTrigger className="h-9 rounded-lg flex-1 text-sm" data-testid="aftercare-template"><SelectValue placeholder="Choose aftercare to send…" /></SelectTrigger>
                    <SelectContent>{acTpls.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}{t.service_name ? ` — ${t.service_name}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="rounded-lg" disabled={sendingAc} onClick={sendAftercare} data-testid="send-aftercare-button"><Send className="h-4 w-4 mr-1.5" />Send</Button>
                </div>
                {acTpls.length === 0 && (
                  <div className="text-xs text-muted-foreground pb-1">No aftercare templates yet — create them under <b>Aftercare Templates</b>.</div>
                )}
                {aftercare.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2">No aftercare sent yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {aftercare.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-sm border-b border-border/50 pb-1" data-testid={`aftercare-${a.id}`}>
                        <span className="flex items-center gap-2">{a.title}
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${aftercareStatus(a).cls}`} data-testid={`aftercare-status-${a.id}`}>{aftercareStatus(a).label}</span>
                          <span className="text-[10px] text-muted-foreground">{a.origin === "automation" ? "auto-sent" : a.sent_via === "sms" ? "via SMS" : "link"}{a.viewed_at ? ` · read ${fmtDate(a.viewed_at)}` : ""}</span>
                        </span>
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <span className="text-xs">{fmtDate(a.created_at)}</span>
                          <button onClick={() => copyAftercare(a)} className="hover:text-foreground" title="Copy link" data-testid={`aftercare-copy-${a.id}`}><LinkIcon className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteAftercare(a.id)} className="hover:text-destructive" data-testid={`aftercare-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="invoices" className="mt-4" data-testid="file-invoices-section">
                <ClientInvoices clientId={file.client.id} cur={cur} isAdmin={isAdmin} />
              </TabsContent>

              <TabsContent value="rewards" className="mt-4" data-testid="file-rewards-section">
                <ClientRewards clientId={file.client.id} isAdmin={isAdmin} onChanged={() => openFile(file.client.id)} />
              </TabsContent>
              <TabsContent value="messages" className="mt-4" data-testid="file-messages-section">
                <div className="rounded-xl border p-3 mb-4" data-testid="file-message-compose">
                  {file.client.phone ? (
                    <>
                      <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={2} placeholder={`Type a quick text to ${file.client.name}…`} className="w-full rounded-lg border border-input bg-background p-3 text-sm min-h-[60px]" data-testid="file-message-input" />
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[11px] text-muted-foreground">Sends by SMS to {file.client.phone} · ≈ {Math.max(1, Math.ceil((msgText.length || 1) / 160))} SMS</span>
                        <Button size="sm" className="rounded-lg" onClick={sendClientMsg} disabled={sendingMsg || !msgText.trim()} data-testid="file-message-send">
                          <Send className="h-4 w-4 mr-1.5" />{sendingMsg ? "Sending…" : "Send message"}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground" data-testid="file-message-nophone">Add a mobile number in Details to text this client.</div>
                  )}
                </div>
                <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-3"><MessageSquare className="h-3.5 w-3.5" />Messages sent ({(file.messages || []).length})</div>
                {(file.messages || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg" data-testid="file-messages-empty">No messages sent to this client yet.</div>
                ) : (
                  <div className="space-y-2">
                    {file.messages.map((m) => {
                      const st = MSG_STATUS[m.status] || { label: m.status || "—", cls: "bg-muted text-muted-foreground border-border" };
                      return (
                        <div key={m.id} className="rounded-lg border p-3" data-testid={`file-message-${m.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{MSG_KIND_LABEL[m.kind] || m.kind || "Message"}</span>
                            <div className="flex items-center gap-2">
                              <span className={`text-[10px] font-medium rounded-full border px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                              <span className="text-[11px] text-muted-foreground tabular-nums">{fmtMsgWhen(m.created_at)}</span>
                            </div>
                          </div>
                          <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-line break-words">{m.body}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
      </div>}
      <FormAnswersDialog clientId={file?.client?.id} instanceId={viewForm} onClose={() => setViewForm(null)} />
      <CancelDialog open={!!cancelB} onOpenChange={(v) => !v && setCancelB(null)} title={`Cancel ${cancelB?.service_name || "booking"}`} description={cancelB ? `${fmtDate(cancelB.start)} · ${file?.client?.name || ""}` : ""} onConfirm={runClientCancel} />
    </div>
  );
}
